'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  type AuthApiUser,
  type AuthFlowResult,
  type AuthProviderId,
  type PendingTwoFactorChallenge,
  loginWithPassword,
  registerWithEmail,
  requestPasswordResetEmail,
  startSocialAuth,
  submitPasswordToken,
  verifyTwoFactorLogin,
} from '@/lib/api/auth';
import { fetchWithAuth } from '@/lib/utils/api-auth';

import {
  buildAuthReturnTo,
  parseAuthUser,
  PENDING_TWO_FACTOR_STORAGE_KEY,
  readStoredPendingChallenge,
} from './AuthContext.helpers';
import { useSessionTimeout } from '@/hooks/auth/useSessionTimeout';

export type AuthUserRole = 'ADMIN' | 'USER' | string;

export type AuthUser = Omit<AuthApiUser, 'role'> & {
  role: AuthUserRole;
};

type AuthActionResult = AuthFlowResult;

type AuthMeResponse = {
  success?: boolean;
  error?: string;
  data?: {
    authenticated?: boolean;
    authRequired?: boolean;
    user?: unknown;
  };
};

type AuthContextType = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authRequired: boolean;
  pendingTwoFactorChallenge: PendingTwoFactorChallenge | null;
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<AuthActionResult>;
  register: (input: { email: string; password?: string; name?: string }) => Promise<AuthActionResult>;
  startSocialLogin: (provider: AuthProviderId, returnTo?: string) => Promise<AuthActionResult>;
  requestPasswordReset: (input: { email: string }) => Promise<AuthActionResult>;
  resetPassword: (input: { token: string; password: string }) => Promise<AuthActionResult>;
  submitTwoFactorChallenge: (input: {
    challengeToken?: string;
    code?: string;
    recoveryCode?: string;
    // M3 — when true, the verify call mints a TrustedDevice
    // row + cookie so future logins from this browser skip the
    // TOTP challenge for 30 days.  See lib/api/auth.ts and
    // server/internal/httpapi/routes/auth_2fa_trusted_device.go.
    rememberDevice?: boolean;
  }) => Promise<AuthActionResult>;
  clearPendingTwoFactorChallenge: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  // Lazy-initialise from sessionStorage so the impure read happens once on
  // mount inside the lazy-init escape hatch — never inside a synchronous
  // useEffect body. The previous "set-state-in-effect" version
  // accomplished the same thing but cost an extra render and tripped the
  // React Compiler's set-state-in-effect rule.
  const [pendingTwoFactorChallenge, setPendingTwoFactorChallenge] = useState<PendingTwoFactorChallenge | null>(() =>
    readStoredPendingChallenge(),
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const persistPendingChallenge = useCallback((next: PendingTwoFactorChallenge | null) => {
    if (typeof window === 'undefined') return;
    if (next) {
      window.sessionStorage.setItem(PENDING_TWO_FACTOR_STORAGE_KEY, JSON.stringify(next));
      return;
    }
    window.sessionStorage.removeItem(PENDING_TWO_FACTOR_STORAGE_KEY);
  }, []);

  const clearPendingTwoFactorChallenge = useCallback(() => {
    if (!mountedRef.current) return;
    setPendingTwoFactorChallenge(null);
    persistPendingChallenge(null);
  }, [persistPendingChallenge]);

  const updatePendingTwoFactorChallenge = useCallback(
    (next: PendingTwoFactorChallenge | null) => {
      if (!mountedRef.current) return;
      setPendingTwoFactorChallenge(next);
      persistPendingChallenge(next);
    },
    [persistPendingChallenge],
  );

  const applyState = useCallback((nextUser: AuthUser | null, nextAuthRequired?: boolean) => {
    if (!mountedRef.current) return;
    setUser(nextUser);
    if (typeof nextAuthRequired === 'boolean') {
      setAuthRequired(nextAuthRequired);
    }
  }, []);

  const refresh = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!mountedRef.current) return;
      // Background revalidation (silent) must NOT flip isLoading. The auth
      // mount-gates (ChatPageRoot, admin layout, …) render a full-screen
      // spinner whenever authLoading is true and unmount the authed tree —
      // which on the chat page tears down and recreates the preview iframe,
      // causing a full reload on every tab-return past the 5-min staleness
      // window. Initial load (silent=false) still flips isLoading so the
      // gate paints the spinner until auth is first confirmed.
      const silent = options?.silent === true;
      if (!silent) {
        setIsLoading(true);
      }
      try {
        const response = await fetchWithAuth('/api/auth/me', {
          cache: 'no-store',
        });
        const payload = (await response.json().catch(() => null)) as AuthMeResponse | null;

        const nextAuthRequired =
          typeof payload?.data?.authRequired === 'boolean' ? payload.data.authRequired : undefined;

        if (!response.ok || payload?.success === false) {
          applyState(null, nextAuthRequired);
          return;
        }

        const authenticated = payload?.data?.authenticated === true;
        const nextUser = authenticated ? parseAuthUser(payload?.data?.user ?? null) : null;
        applyState(nextUser, nextAuthRequired);
      } catch {
        applyState(null);
      } finally {
        if (mountedRef.current && !silent) {
          setIsLoading(false);
        }
      }
    },
    [applyState],
  );

  // Initial /api/auth/me probe. Dispatched through a microtask so
  // refresh's synchronous setIsLoading(true) at the top of its body lands
  // *after* the current render commit — the canonical shape that
  // satisfies `react-hooks/set-state-in-effect` for "defer an imperative
  // load on mount".
  useEffect(() => {
    void Promise.resolve().then(() => refresh());
  }, [refresh]);

  // Session-lifecycle listeners — the global 401 handler (Fix 1) and the
  // visibility-return revalidation (Fix 2) — extracted to useSessionTimeout
  // (Phase 1.6). Both effects moved verbatim; mountedRef stays provider-owned and
  // is injected alongside the user / applyState / clearPendingTwoFactorChallenge /
  // refresh collaborators.
  useSessionTimeout({ user, applyState, clearPendingTwoFactorChallenge, refresh, mountedRef });

  const login = useCallback(
    async (input: { email: string; password: string }): Promise<AuthActionResult> => {
      const result = await loginWithPassword(input);
      if (!result.ok) {
        return result;
      }

      if (result.nextStep === 'two-factor' && result.twoFactorChallenge) {
        updatePendingTwoFactorChallenge(result.twoFactorChallenge);
        return result;
      }

      clearPendingTwoFactorChallenge();
      if (result.nextStep === 'authenticated') {
        applyState(parseAuthUser(result.user ?? null));
        await refresh();
      }
      return result;
    },
    [applyState, clearPendingTwoFactorChallenge, refresh, updatePendingTwoFactorChallenge],
  );

  const register = useCallback(
    async (input: { email: string; password?: string; name?: string }): Promise<AuthActionResult> => {
      const result = await registerWithEmail(input);
      if (!result.ok) {
        return result;
      }

      if (result.nextStep === 'authenticated') {
        clearPendingTwoFactorChallenge();
        applyState(parseAuthUser(result.user ?? null));
        await refresh();
      }
      return result;
    },
    [applyState, clearPendingTwoFactorChallenge, refresh],
  );

  const startSocialLogin = useCallback(
    async (provider: AuthProviderId, returnTo?: string): Promise<AuthActionResult> => {
      const result = await startSocialAuth(provider, returnTo ?? buildAuthReturnTo());
      if (!result.ok) {
        return result;
      }

      if (typeof window === 'undefined') {
        return {
          ok: false,
          error: 'Social sign-in is only available in the browser.',
        };
      }

      window.location.assign(result.redirectUrl);
      const providerLabel = provider === 'google' ? 'Google' : provider === 'github' ? 'GitHub' : 'Microsoft';
      return {
        ok: true,
        message: `Redirecting to ${providerLabel}...`,
      };
    },
    [],
  );

  const requestPasswordReset = useCallback(
    async (input: { email: string }): Promise<AuthActionResult> => requestPasswordResetEmail(input),
    [],
  );

  const resetPassword = useCallback(
    async (input: { token: string; password: string }): Promise<AuthActionResult> =>
      submitPasswordToken({
        token: input.token,
        password: input.password,
        preferredKind: 'reset-password',
      }),
    [],
  );

  const submitTwoFactorChallenge = useCallback(
    async (input: {
      challengeToken?: string;
      code?: string;
      recoveryCode?: string;
      rememberDevice?: boolean;
    }): Promise<AuthActionResult> => {
      const challengeToken = input.challengeToken?.trim() || pendingTwoFactorChallenge?.challengeToken || '';
      const result = await verifyTwoFactorLogin({
        challengeToken,
        code: input.code,
        recoveryCode: input.recoveryCode,
        rememberDevice: input.rememberDevice,
      });

      if (!result.ok) {
        return result;
      }

      clearPendingTwoFactorChallenge();
      if (result.nextStep === 'authenticated') {
        applyState(parseAuthUser(result.user ?? null));
        await refresh();
      }
      return result;
    },
    [applyState, clearPendingTwoFactorChallenge, pendingTwoFactorChallenge, refresh],
  );

  const logout = useCallback(async () => {
    try {
      await fetchWithAuth('/api/auth/logout', {
        method: 'POST',
      });
    } catch {
      // best-effort
    } finally {
      clearPendingTwoFactorChallenge();
      applyState(null);
      await refresh();
    }
  }, [applyState, clearPendingTwoFactorChallenge, refresh]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      authRequired,
      pendingTwoFactorChallenge,
      refresh,
      login,
      register,
      startSocialLogin,
      requestPasswordReset,
      resetPassword,
      submitTwoFactorChallenge,
      clearPendingTwoFactorChallenge,
      logout,
    }),
    [
      user,
      isLoading,
      authRequired,
      pendingTwoFactorChallenge,
      refresh,
      login,
      register,
      startSocialLogin,
      requestPasswordReset,
      resetPassword,
      submitTwoFactorChallenge,
      clearPendingTwoFactorChallenge,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
